import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router'
import useIsMountedRef from 'use-is-mounted-ref'

import { WebexAuthLanguage } from '../../../../../i18n'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { configureFullURLForLocalDevelopment } from '../../../../React/Common/Utils/LocalUtils'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState, UserInfoContext } from '../../../../React/Common/Contexts'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { OAuthConfig } from '../../../../React/Common/Components/OAuthButton/Types/Types'
import { TelSysAdminDTO } from '../../../../NonReact/Types/Types'
import { AUTH_ROUTE_PATH } from '../../../Constants/Constants'
import { signOut } from '../../../../React/Common/API/UserSignInOutAPI'
import { OAuthButton } from '../../../../React/Common/Components/OAuthButton/OAuthButton'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'
import Icon, { IconName } from '../../../../React/Common/Components/Icon'
import Loader from '../../../../React/Common/Components/Loader'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { DarkModeType, useDarkMode } from '../../../../React/Common/Hooks/useDarkMode'
import { useBranding } from '../../../../React/Common/Hooks/useBranding'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { LanguageUtils } from '../../../../React/Common/Utils/LanguageUtils'
import { UserInfoContextType } from '../../../../React/Common/Contexts/UserInfoContext'
import { getOAuthConfig } from '../../../../React/Common/API/ConfigAPI'
import { getTelSys } from '../../../../NonReact/common/API/EntityAPIClient'
import { requestJSON } from '../../../../NonReact/common/API/REST/RESTClient'
import { telSysUpdateURL } from '../../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../../NonReact/constants/REST'
import MessageContext, { MessageContextType } from '../../../../React/Common/Contexts/MessageContext'

export const WebexAuth: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(WebexAuthLanguage.NAME)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const { displayError }: MessageContextType = useContext(MessageContext)
    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const branding: Nullable<Branding> = useBranding()
    const navigate: Nullable<NavigateFunction> = useNavigate()
    const location: Nullable<Location> = useLocation()
    const { isDarkMode }: DarkModeType = useDarkMode()

    const [error, setError]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [isAuthenticated, setIsAuthenticated]: ReactState<boolean> = useState<boolean>(false)
    const [isAuthenticating, setIsAuthenticating]: ReactState<boolean> = useState<boolean>(false)
    const [config, setConfig]: ReactState<Nullable<OAuthConfig>> = useState<Nullable<OAuthConfig>>()
    const [telSys, setTelSys]: ReactState<Nullable<TelSysAdminDTO>> = useState<Nullable<TelSysAdminDTO>>()
    const [statusesToDisplay, setStatusesToDisplay]: ReactState<Nullable<ReactElement[]>> =
        useState<Nullable<ReactElement[]>>()

    const mainLogoURL: string = useMemo(
        (): string =>
            (isDarkMode
                ? branding?.BrandingConfig?.imagery?.mainLogo
                : branding?.BrandingConfig?.imagery?.mainLogoForWhiteBG) ?? EMPTY_STRING,
        [isDarkMode, branding]
    )

    const onAuthenticating = (): void => {
        setError(undefined)
        setIsAuthenticated(false)
        setIsAuthenticating(true)
    }

    const onOAuthFailure = (e?: Error): void => {
        setError(e?.message ?? language.get(WebexAuthLanguage.WebexAuth_Error_Unknown))
        setIsAuthenticating(false)
    }

    const signoutAndRedirectToLogin = (): void =>
        signOut(
            (): void =>
                navigate?.(AUTH_ROUTE_PATH, {
                    replace: true,
                    state: { location }
                })
        )

    const onOAuthSuccess = (sessionID?: number): void => {
        if (IsVarEmpty(telSys?.TelSysID)) {
            onOAuthFailure()
            return
        }

        requestJSON<void, TelSysAdminDTO>(telSysUpdateURL(telSys?.TelSysID), {
            method: RESTMethods.PUT,
            body: {
                ...telSys,
                AuthPassword2OAuthSessionID: sessionID?.toString(),
                // The scope was requested under the saved call recording setting (see the config request below).
                CallRecordingAuthorised: !!telSys?.CallRecordingEnabled
            }
        })
            .then((): void => {
                if (isMounted.current) {
                    setIsAuthenticating(false)
                    setIsAuthenticated(true)

                    setTimeout((): void => signoutAndRedirectToLogin(), 1000)
                }
            })
            .catch(onOAuthFailure)
    }

    useEffect((): void => {
        if (IsVarEmpty(userInfo)) return

        getTelSys(userInfo?.Scope?.TelSysID[0])
            .then((telephonyServer: Nullable<TelSysAdminDTO>): void => {
                if (!isMounted.current) return

                setTelSys(telephonyServer)

                // The scope depends on the server's call recording setting, so the config waits for the server.
                getOAuthConfig(TelSysTypes.WEBEX_CALLING, {
                    callRecordingEnabled: !!telephonyServer?.CallRecordingEnabled
                })
                    .then((oAuthConfig: Nullable<OAuthConfig>): void => {
                        if (isMounted.current) {
                            setConfig(oAuthConfig)
                        }
                    })
                    .catch(displayError)
            })
            .catch((e: Error): void => {
                if (isMounted.current) {
                    displayError(e)
                    setTimeout(signOut, 2000)
                }
            })
    }, [userInfo])

    return (
        <div className={`router-teams-auth ${isAuthenticating ? 'is-authenticating' : ''}`}>
            <div className='ta-logo-container'>
                <img
                    className='ta-logo'
                    src={configureFullURLForLocalDevelopment(mainLogoURL)}
                    alt={branding?.BrandName ?? 'Company logo'}
                />

                {isAuthenticating ? <Loader className='authenticating-loader' /> : <></>}

                <div className='link-icon-container'>
                    {isAuthenticated ? (
                        <Icon className='success-icon' iconName={IconName.SUCCESS} />
                    ) : (
                        <Icon iconName={IconName.NOT_LINKED} />
                    )}
                </div>

                <img className='ta-logo' src='/CCS/Styles/Images/webex-logo.png' alt='Webex Logo' />
            </div>

            <div className='ta-intro ta-intro-1'>
                {LanguageUtils.formatLanguageEntry(
                    language.getInterpolated(
                        WebexAuthLanguage.WebexAuth_UserIntro,
                        isStringNotEmpty(userInfo?.FullName) ? userInfo?.FullName : userInfo?.Username
                    )
                )}
            </div>

            <div className='ta-intro'>{language.get(WebexAuthLanguage.WebexAuth_UserIntro_2)}</div>

            {isStringNotEmpty(error) && IsVarEmpty(statusesToDisplay) ? <div className='ta-error'>{error}</div> : <></>}

            {isVarNotEmpty(statusesToDisplay) && statusesToDisplay.length > 0 ? (
                <div className='ta-statuses'>
                    {statusesToDisplay.map(
                        (status: ReactElement): ReactElement => (
                            <div key={status.key} className='ta-status'>
                                {status}
                            </div>
                        )
                    )}
                </div>
            ) : (
                <></>
            )}

            {isVarNotEmpty(config) && isVarNotEmpty(telSys) ? (
                <div className={`ta-action-container`}>
                    <RoundedButton
                        className='ta-decline'
                        onClick={(): void =>
                            signOut(
                                (): void =>
                                    navigate?.(AUTH_ROUTE_PATH, {
                                        replace: true,
                                        state: { location }
                                    })
                            )
                        }
                        isOutlined
                        disabled={isAuthenticating || isAuthenticated}
                        shrink
                    >
                        {language.get(WebexAuthLanguage.WebexAuth_Decline)}
                    </RoundedButton>
                    <OAuthButton
                        selectedTelSys={telSys?.TelSysID}
                        integrationType={TelSysTypes.WEBEX_CALLING}
                        config={config}
                        onAuthenticating={onAuthenticating}
                        onOAuthDisplayStatus={(statuses: ReactElement[]): void => setStatusesToDisplay(statuses)}
                        onOAuthSuccess={onOAuthSuccess}
                        onOAuthFailure={onOAuthFailure}
                        oAuthMessageOptions={{
                            preAuthorisationButtonTitle: language.get(WebexAuthLanguage.WebexAuth_AdminConsent),
                            preAuthorisedMessage: language.get(WebexAuthLanguage.WebexAuth_ConsentedMessage),
                            authorisationButtonTitle: language.get(WebexAuthLanguage.WebexAuth_Authorise),
                            authorisedMessage: language.get(WebexAuthLanguage.WebexAuth_AuthorisedMessage)
                        }}
                        isRouterMode
                        shrink
                    />
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}
